import { Box, Button, Stack, Typography } from '@mui/material';
import { get, map } from 'lodash';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, getFullName, getProfileImage } from '../../common/utils/format-utils';
import { Icons } from '../icons';
import { JobsHistory } from '../jobs-history';
import { Loader } from '../loader';
import { Page } from '../page';

interface PersonalInfoProps {
  data?: {
    birthPlace?: string;
    citizenship?: string;
    dob: string;
    email?: string;
    firstName: string;
    gender: string;
    id?: number;
    lastName: string;
    middleName: string;
    nationality: string;
    organization?: string | null;
    passportExpireDate: string;
    passportIssueDate: string;
    passportIssuePlace: string;
    passportNumber: string | number;
    passportPhoto?: string | null | undefined;
    passportSeries: string;
    perAddress?: string;
    phone?: string;
    pin: string | number;
    role?: string;
    tin: string | number;
  };
  isLoading?: boolean;
  onUpdate?: () => void;
}

export const PersonalInfo: ComponentType<PersonalInfoProps> = ({
  data = {},
  isLoading = false,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const {
    pin,
    tin,
    dob,
    gender,
    passportPhoto,
    passportNumber,
    passportSeries,
    passportIssuePlace,
    passportIssueDate,
    passportExpireDate,
  } = data || {};

  const fullName = getFullName(data);

  const profileImage = getProfileImage(passportPhoto);
  const columns = [
    {
      pin,
      tin,
      dob: dob ? formatDate(dob) : null,
      gender: gender ? t(`gender.${gender}`) : t('gender.none', 'Не определен'),
    },
    {
      passportSerialNumber: [passportSeries, passportNumber].filter(Boolean).join(''),
      passportIssuePlace,
      passportIssueDate: passportIssueDate ? formatDate(passportIssueDate) : null,
      passportExpireDate: passportExpireDate ? formatDate(passportExpireDate) : null,
    },
  ];

  const handleUpdate = () => {
    if (onUpdate) onUpdate();
  };

  const view = () => {
    if (isLoading) return <Loader minHeight="50vh" />;

    return (
      <Stack spacing={4}>
        <Stack direction="row" alignItems="flex-start">
          <Box mr={3} sx={{ mr: 3, borderRadius: 1, display: 'flex', overflow: 'hidden' }}>
            <img
              src={profileImage}
              alt={fullName}
              width="160"
              height="192"
              style={{ objectFit: 'cover' }}
            />
          </Box>
          <Box>
            <Typography variant="h2" mb={3}>
              {fullName}
            </Typography>
            <Stack spacing={8.75} direction="row">
              {map(columns, (col, idx) => (
                <Box key={idx}>
                  {map(Object.keys(col), (key) => (
                    <Typography variant="subtitle2" component="div" key={key} mb={2}>
                      <Typography variant="body2" component="span" color="grey.600" mr={1}>
                        {t(`profile.${key}`)}:
                      </Typography>
                      {get(col, key)}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Stack>
            <Button
              onClick={handleUpdate}
              variant="outlined"
              color="secondary"
              size="large"
              sx={{ mt: 1, py: 1.25, px: 1.5 }}
              startIcon={<Icons.RefreshDouble />}
            >
              <Typography variant="button_lg" px={1.25}>
                {t('action.updateData', 'Обновить данные')}
              </Typography>
            </Button>
          </Box>
        </Stack>
        <JobsHistory pin={pin} />
      </Stack>
    );
  };

  return <Page title={t('personalCabinet', 'Личный кабинет')}>{view()}</Page>;
};
